import { Application } from '@/types/application';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Button, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { type FC } from 'react';
import ApplicationPicker from './ApplicationPicker';

export type Side = 'A' | 'B';

type ApplicationPickersContainerProps = {
  appA: Application | undefined;
  appB: Application | undefined;
  labelA: string;
  labelB: string;
  setLabelA: (newLabel: string) => void;
  setLabelB: (newLabel: string) => void;
  setAppA: (newApp: Application | undefined) => void;
  setAppB: (newApp: Application | undefined) => void;
  viewResults: (value: boolean) => void;
};

const ApplicationPickersContainer: FC<ApplicationPickersContainerProps> = ({
  appA,
  appB,
  labelA,
  labelB,
  setAppA,
  setAppB,
  setLabelA,
  setLabelB,
  viewResults
}) => {
  const isSmallScreen = useMediaQuery('(max-width: 900px)');

  const handleSwap = () => {
    setAppA(appB);
    setAppB(appA);
    setLabelA(labelB);
    setLabelB(labelA);
  };

  const handleAppSelected = (side: Side, app?: Application, label?: string) => {
    if (side === 'A') {
      setAppA(app);
      setLabelA(label || '');
    } else {
      setAppB(app);
      setLabelB(label || '');
    }
  };

  const handleReset = () => {
    setAppA(undefined);
    setAppB(undefined);
    setLabelA('');
    setLabelB('');
  };

  return (
    <Stack width={isSmallScreen ? 400 : 900} justifySelf='center' alignItems='center'>
      <Typography variant='h5' mt={4} mb={3}>
        Compare two BlockEngine applications
      </Typography>
      <Box alignSelf='flex-end' mb={1}>
        <Button size='small' color='inherit' disabled={!appA && !appB} onClick={handleReset}>
          Clear selections
        </Button>
      </Box>
      <Stack width='100%' gap={1} flexDirection={isSmallScreen ? 'column' : 'row'}>
        <ApplicationPicker
          side='A'
          label={labelA}
          isSmallScreen={isSmallScreen}
          selectedAppId={appA?.applicationId || appB?.applicationId}
          onAppSelected={handleAppSelected}
        />
        <Tooltip title='Swap A and B'>
          <span style={{ height: 'fit-content', alignSelf: 'center' }}>
            <IconButton onClick={handleSwap} disabled={!appA && !appB} size='large' color='primary'>
              <SwapHorizIcon />
            </IconButton>
          </span>
        </Tooltip>
        <ApplicationPicker
          side='B'
          label={labelB}
          isSmallScreen={isSmallScreen}
          selectedAppId={appB?.applicationId || appA?.applicationId}
          onAppSelected={handleAppSelected}
        />
      </Stack>
      <Box display='flex' justifyContent='center' mt={3}>
        <Button
          variant='contained'
          startIcon={<CompareArrowsIcon />}
          disabled={!appA || !appB}
          onClick={() => viewResults(true)}
        >
          Compare
        </Button>
      </Box>
    </Stack>
  );
};

export default ApplicationPickersContainer;
